import { useState, useEffect, useRef } from 'react';
import { Position, PositionAnalysis, DEFAULT_SELL_TIERS } from '@/types/portfolio';
import { fetchMultipleStockPrices } from '@/lib/stockApi';
import { analyzePosition, calculatePortfolioStats } from '@/lib/portfolioAnalysis';
import { fetchExchangeRates, ExchangeRates, convertToCHF } from '@/lib/currencyApi';
import { PortfolioStats } from '@/components/PortfolioStats';
import { PositionCard } from '@/components/PositionCard';
import { StockTable } from '@/components/StockTable';
import { StockScorecard } from '@/components/StockScorecard';
import { AddPositionDialog } from '@/components/AddPositionDialog';
import { ExcelImport } from '@/components/ExcelImport';
import { Auth } from '@/components/Auth';
import { FibonacciRetracement } from '@/components/FibonacciRetracement';
import { PortfolioGainsPieChart } from '@/components/PortfolioGainsPieChart';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { RefreshCw, TrendingUp, LogOut, ArrowUpDown, Clock, FileBarChart, Trash2, ClipboardList, Search, HelpCircle, Sparkles, Moon, Sun, BarChart3, Upload, Database, ShieldCheck, DollarSign, Activity, Bell, ShieldAlert, CalendarDays, Globe, Landmark, Eye, Grid3X3, User, LineChart, Briefcase, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { ThemeToggle } from '@/components/ThemeToggle';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import tradeCompassLogo from '@/assets/tradecompass-logo.png';
import { AutoSaveIndicator } from '@/components/AutoSaveIndicator';
import { AIChatWidget } from '@/components/AIChatWidget';
import { SavedDataMenu } from '@/components/SavedDataMenu';
import { useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { DASHBOARD_MODULES } from '@/config/dashboardModules';
import { DashboardModuleCard } from '@/components/DashboardModuleCard';
import { ModuleAuditPanel } from '@/components/ModuleAuditPanel';

type SortOption = 'name' | 'totalValue' | 'gainLoss' | 'addedValue';

const Index = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isStockPriceGatheringActive = location.pathname === '/stock-price-gathering';
  const [user, setUser] = useState<any>(null);
  const [positions, setPositions] = useState<Position[]>([]);
  const [analyses, setAnalyses] = useState<PositionAnalysis[]>([]);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState<SortOption>('name');
  const [exchangeRates, setExchangeRates] = useState<ExchangeRates>({ USD: 0.88, EUR: 0.95, CHF: 1 });
  const [timeUntilRefresh, setTimeUntilRefresh] = useState(3600); // 60 minutes in seconds
  const [refreshStatus, setRefreshStatus] = useState<'idle' | 'loading' | 'success' | 'mixed' | 'error'>('idle');
  const [successRate, setSuccessRate] = useState(100);
  const [fetchProgress, setFetchProgress] = useState(0);
  const { toast } = useToast();
  const cardRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const modulesGridRef = useRef<HTMLDivElement | null>(null);
  const [moduleSearch, setModuleSearch] = useState('');
  const [moduleCategory, setModuleCategory] = useState<string>('all');
  const [moduleMatchCount, setModuleMatchCount] = useState<number | null>(null);

  useEffect(() => {
    const grid = modulesGridRef.current;
    if (!grid) return;
    const q = moduleSearch.trim().toLowerCase();
    const cat = moduleCategory;
    const cards = Array.from(grid.children) as HTMLElement[];
    let visible = 0;
    cards.forEach((card) => {
      const text = (card.textContent || '').toLowerCase();
      const cardCat = card.dataset.category || '';
      const matchesQuery = q === '' || text.includes(q);
      const matchesCat = cat === 'all' || cardCat === cat;
      const show = matchesQuery && matchesCat;
      card.style.display = show ? '' : 'none';
      if (show) visible++;
    });
    setModuleMatchCount(q === '' && cat === 'all' ? null : visible);
  }, [moduleSearch, moduleCategory]);


  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const removeDuplicates = async () => {
    if (!user) return;
    
    // Get all positions
    const { data: allPositions } = await supabase
      .from('positions')
      .select('*')
      .order('created_at', { ascending: true });
    
    if (!allPositions) return;
    
    // Group by ticker and keep only the first one
    const seen = new Set<string>();
    const duplicateIds: string[] = [];
    
    allPositions.forEach(pos => {
      if (seen.has(pos.ticker)) {
        duplicateIds.push(pos.id);
      } else {
        seen.add(pos.ticker);
      }
    });
    
    // Delete duplicates if any found
    if (duplicateIds.length > 0) {
      await supabase
        .from('positions')
        .delete()
        .in('id', duplicateIds);
      
      toast({
        title: "Duplicates Removed",
        description: `Removed ${duplicateIds.length} duplicate position${duplicateIds.length !== 1 ? 's' : ''}`,
      });
    }
  };

  const loadPositions = async () => {
    if (!user) return;
    
    const { data, error } = await supabase
      .from('positions')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      toast({
        title: "Error Loading Positions",
        description: error.message,
        variant: "destructive"
      });
      return;
    }

    setPositions(data.map(p => ({
      id: p.id,
      ticker: (p as any).stock_name || p.ticker,
      buyPrice: Number(p.buy_price),
      quantity: p.quantity,
      totalValue: p.total_value ? Number(p.total_value) : undefined,
      localCurrency: p.local_currency || undefined,
      gainPct: p.gain_pct ? Number(p.gain_pct) : undefined,
      addedValueChf: p.added_value_chf ? Number(p.added_value_chf) : undefined,
      pricePerShare: p.price_per_share ? Number(p.price_per_share) : undefined,
      isin: p.isin || undefined,
      stockCode: p.ticker,
      initialInvestment: (p as any).initial_investment ? Number((p as any).initial_investment) : undefined,
    })));
  };

  useEffect(() => {
    const initializeData = async () => {
      await removeDuplicates();
      await loadPositions();
    };
    
    initializeData();
    
    // Fetch exchange rates on mount and every hour
    const loadRates = async () => {
      const rates = await fetchExchangeRates();
      setExchangeRates(rates);
    };
    
    loadRates();
    const interval = setInterval(loadRates, 60 * 60 * 1000); // Every hour
    
    return () => clearInterval(interval);
  }, [user]);
  
  const fetchPrices = async () => {
    if (positions.length === 0) {
      setAnalyses([]);
      return;
    }
    
    setLoading(true);
    setRefreshStatus('loading');
    setFetchProgress(0);
    try {
      // IG Markets API disabled - using Excel data only
      const prices = new Map(); // Empty price map
      setFetchProgress(100);
      
      let successCount = 0;
      let totalCount = positions.length;
      
      const newAnalyses = positions.map(position => {
        const symbolToUse = position.stockCode || position.ticker;
        const cleanedTicker = symbolToUse
          .replace(/\s*\(.*?\)\s*/g, '')
          .replace(/\s*\|.*$/g, '')
          .replace(/\s*-[A-Z]-\s*/g, '')
          .replace(/\s+/g, '')
          .toUpperCase()
          .trim();
        
        const priceData = prices.get(cleanedTicker);
        const currentPrice = priceData?.price && priceData.price > 0 
          ? priceData.price 
          : (position.pricePerShare || position.buyPrice);
        
        // Count successful fetches
        if (priceData?.price && priceData.price > 0) {
          successCount++;
        }
        
        // Create analysis with Excel data if available
        const analysis = analyzePosition(position, currentPrice, exchangeRates);
        
        // If Excel data provides gainPct, recalculate recovery metrics
        let finalAnalysis = analysis;
        if (position.gainPct !== undefined) {
          const gainPctValue = position.gainPct * 100; // Convert decimal to percentage
          const investedValue = position.buyPrice * position.quantity;
          
          // Find the highest tier that qualifies
          let cumulativeRecoverPct = 0;
          let amountToRecover = 0;
          let matchedTier = null;
          
          for (const tier of DEFAULT_SELL_TIERS.sort((a, b) => b.gainPct - a.gainPct)) {
            if (gainPctValue >= tier.gainPct) {
              matchedTier = tier;
              cumulativeRecoverPct = tier.cumulativeRecoverPct;
              amountToRecover = investedValue * (cumulativeRecoverPct / 100);
              break;
            }
          }
          
          // Calculate shares to sell - use direct percentage if specified
          let recommendedSellQty = 0;
          if (matchedTier?.sellQtyPct !== undefined) {
            recommendedSellQty = Math.floor(position.quantity * (matchedTier.sellQtyPct / 100));
          } else if (amountToRecover > 0) {
            recommendedSellQty = Math.floor(amountToRecover / currentPrice);
          }
          
          const recommendedSellPct = position.quantity > 0
            ? Math.round((recommendedSellQty / position.quantity) * 100)
            : 0;
          
          const currency = position.localCurrency || 'CHF';
          const capitalRecovered = amountToRecover;
          const capitalRecoveredCHF = convertToCHF(capitalRecovered, currency, exchangeRates);
          const remainingCapitalAtRisk = Math.max(0, investedValue - capitalRecovered);
          
          finalAnalysis = {
            ...analysis,
            gainPct: position.gainPct,
            recommendedSellQty,
            recommendedSellPct,
            capitalRecovered: Math.round(capitalRecovered * 100) / 100,
            capitalRecoveredCHF: Math.round(capitalRecoveredCHF * 100) / 100,
            capitalRecoveredPct: cumulativeRecoverPct,
            remainingCapitalAtRisk: Math.round(remainingCapitalAtRisk * 100) / 100
          };
        }
        
        // Override with Excel data if present
        return {
          ...finalAnalysis,
          totalValue: position.totalValue || finalAnalysis.totalValue,
          localCurrency: position.localCurrency,
          gainAmount: position.gainPct !== undefined 
            ? (position.totalValue || finalAnalysis.totalValue) * position.gainPct 
            : finalAnalysis.gainAmount,
          addedValueChf: position.addedValueChf,
          pricePerShare: position.pricePerShare,
          currentPrice,
          isin: position.isin,
          stockCode: position.stockCode
        };
      });
      
      // Calculate success rate
      const rate = (successCount / totalCount) * 100;
      setSuccessRate(rate);
      
      // Set status based on success rate
      if (rate === 100) {
        setRefreshStatus('success');
      } else if (rate === 0) {
        setRefreshStatus('error');
      } else {
        setRefreshStatus('mixed');
      }
      
      setAnalyses(newAnalyses);
      toast({
        title: "Data Loaded",
        description: `Using Excel data for ${totalCount} positions`,
      });
      
      // Reset status after 3 seconds
      setTimeout(() => setRefreshStatus('idle'), 3000);
    } catch (error) {
      setRefreshStatus('error');
      toast({
        title: "Error Fetching Prices",
        description: "Failed to update stock prices. Please try again.",
        variant: "destructive"
      });
      // Reset status after 3 seconds
      setTimeout(() => setRefreshStatus('idle'), 3000);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchPrices();
    setTimeUntilRefresh(3600); // Reset countdown to 60 minutes
    
    // Auto-refresh prices every 60 minutes
    const interval = setInterval(() => {
      fetchPrices();
      setTimeUntilRefresh(3600); // Reset countdown
    }, 3600000); // 60 minutes for IG API updates
    
    return () => clearInterval(interval);
  }, [positions]);
  
  // Countdown timer
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeUntilRefresh((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    
    return () => clearInterval(timer);
  }, []);
  
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  const handleAddPosition = async (newPosition: Omit<Position, 'id'>) => {
    if (!user) return;
    
    const { error } = await supabase
      .from('positions')
      .insert({
        ticker: newPosition.ticker,
        buy_price: newPosition.buyPrice,
        quantity: newPosition.quantity,
        user_id: user.id
      });

    if (error) {
      toast({
        title: "Error Adding Position",
        description: error.message,
        variant: "destructive"
      });
      return;
    }

    await loadPositions();
    toast({
      title: "Position Added",
      description: `Added ${newPosition.ticker} to portfolio`,
    });
  };
  
  const handleRefreshPrices = () => {
    fetchPrices();
    setTimeUntilRefresh(3600); // Reset countdown on manual refresh
  };
  
  const handleDeletePosition = async (id: string) => {
    const { error } = await supabase
      .from('positions')
      .delete()
      .eq('id', id);

    if (error) {
      toast({
        title: "Error Removing Position",
        description: error.message,
        variant: "destructive"
      });
      return;
    }

    await loadPositions();
    toast({
      title: "Position Removed",
      description: "Position removed from portfolio",
    });
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };

  const handleResetData = async () => {
    if (!user) return;
    
    const { error } = await supabase
      .from('positions')
      .delete()
      .eq('user_id', user.id);

    if (error) {
      toast({
        title: "Error Resetting Data",
        description: error.message,
        variant: "destructive"
      });
      return;
    }

    await loadPositions();
    toast({
      title: "Data Reset",
      description: "All positions have been cleared from the database",
    });
  };

  const scrollToCard = (id: string) => {
    const element = cardRefs.current[id];
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Add a brief highlight effect
      element.classList.add('ring-4', 'ring-primary', 'ring-opacity-50');
      setTimeout(() => {
        element.classList.remove('ring-4', 'ring-primary', 'ring-opacity-50');
      }, 2000);
    }
  };

  if (!user) {
    return <Auth />;
  }
  
  const stats = analyses.length > 0 
    ? calculatePortfolioStats(analyses, exchangeRates)
    : { totalInvested: 0, totalValue: 0, totalValueCHF: 0, totalInvestedCHF: 0, totalAddedValueCHF: 0, initialTotalInvestment: 0, totalGain: 0, totalGainPct: 0, positionCount: 0 };
  
  // Calculate alert counts by threshold (15%, 30%, 45%)
  const alertCounts = analyses.reduce((acc, analysis) => {
    const gainPct = analysis.gainPct;
    if (gainPct >= 45) acc.red++;      // 100% recovery
    else if (gainPct >= 30) acc.orange++; // 40% recovery
    else if (gainPct >= 15) acc.yellow++; // 20% recovery
    return acc;
  }, { yellow: 0, orange: 0, red: 0 });
  
  // Sort analyses based on selected option
  const sortedAnalyses = [...analyses].sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return a.ticker.localeCompare(b.ticker);
      case 'totalValue':
        return (b.totalValue || 0) - (a.totalValue || 0);
      case 'gainLoss':
        return (b.gainPct || 0) - (a.gainPct || 0);
      case 'addedValue':
        return (b.addedValueChf || 0) - (a.addedValueChf || 0);
      default:
        return 0;
    }
  });
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/30">
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-accent/5 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-success/3 rounded-full blur-3xl" />
      </div>
      
      {/* Progress Bar */}
      {loading && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-md border-b border-border shadow-lg">
          <div className="container mx-auto px-4 py-3">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-foreground min-w-[180px]">
                Refreshing stock prices...
              </span>
              <Progress value={fetchProgress} className="flex-1" />
              <span className="text-sm font-semibold text-primary min-w-[50px] text-right">
                {fetchProgress}%
              </span>
            </div>
          </div>
        </div>
      )}
      
      {/* Status Bar */}
      {!loading && refreshStatus !== 'idle' && (
        <div className={cn(
          "fixed top-0 left-0 right-0 h-1 z-50 transition-all duration-300",
          refreshStatus === 'success' && "bg-success",
          refreshStatus === 'mixed' && "bg-orange-500",
          refreshStatus === 'error' && "bg-destructive"
        )}>
          <div className="h-full transition-all duration-500" style={{ width: '100%' }} />
        </div>
      )}
      
      <div className="relative container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="flex flex-col gap-6 mb-10">
          {/* Top Bar */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gradient-to-br from-primary to-primary/80 rounded-2xl shadow-lg">
                <TrendingUp className="h-8 w-8 text-primary-foreground" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-bold text-foreground flex items-center gap-2">
                  GainTracker
                  <Sparkles className="h-5 w-5 text-accent" />
                </h1>
                <div className="flex items-center gap-2 mt-1">
                  <img 
                    src={tradeCompassLogo} 
                    alt="TradeCompass" 
                    className="h-5 w-auto opacity-80"
                  />
                  <span className="text-xs text-muted-foreground font-medium">
                    Provided by TradeCompass
                  </span>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-muted/50 rounded-full border border-border">
                <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
                <span className="text-xs text-muted-foreground font-medium">User:</span>
                <span className="text-xs font-semibold text-foreground truncate max-w-[180px]">
                  {user?.email || 'Unknown'}
                </span>
              </div>
              <SavedDataMenu />
              <AutoSaveIndicator />
              <ThemeToggle />
              <Button variant="ghost" size="sm" onClick={() => navigate('/help')} className="gap-2">
                <HelpCircle className="h-4 w-4" />
                <span className="hidden sm:inline">Help</span>
              </Button>
              <Button variant="ghost" size="sm" onClick={() => navigate('/profile')} className="gap-2">
                <User className="h-4 w-4" />
                <span className="hidden sm:inline">Profile</span>
              </Button>
              <Button variant="ghost" size="sm" onClick={handleSignOut} className="gap-2">
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:inline">Sign Out</span>
              </Button>
            </div>
          </div>
          
          {/* Navigation Cards - Large Centered Icons */}
          <div className="flex justify-center">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6 w-full max-w-6xl">
              {(([
                { key: 'refresh', label: 'Refresh', icon: RefreshCw, color: 'text-primary', borderHover: 'hover:border-primary/40', onClick: handleRefreshPrices, iconClass: cn(loading && 'animate-spin') },
                { key: 'ibkr', label: 'IBKR Analysis', icon: FileBarChart, color: 'text-accent', borderHover: 'hover:border-accent/40', path: '/ibkr-import' },
                { key: 'content', label: 'Content Summary', icon: Sparkles, color: 'text-red-500', borderHover: 'hover:border-red-500/40', path: '/content-summary' },
                { key: 'db', label: 'DB Order', icon: ClipboardList, color: 'text-orange-600 dark:text-orange-400', borderHover: 'hover:border-orange-500/40', path: '/db-order' },
                { key: 'sma', label: 'IBKR Surveillance', icon: BarChart3, color: 'text-purple-600 dark:text-purple-400', borderHover: 'hover:border-purple-500/40', path: '/sma-surveillance' },
                { key: 'excel', label: 'Excel Import', icon: Upload, color: 'text-cyan-600 dark:text-cyan-400', borderHover: 'hover:border-cyan-500/40', onClick: () => document.getElementById('excel-import-trigger')?.click() },
                { key: 'collected', label: 'Collected Data', icon: Database, color: 'text-teal-600 dark:text-teal-400', borderHover: 'hover:border-teal-500/40', path: '/collected-data' },
                { key: 'hedge', label: 'Hedge Protection', icon: ShieldCheck, color: 'text-emerald-600 dark:text-emerald-400', borderHover: 'hover:border-emerald-500/40', path: '/hedge-protection' },
                { key: 'div', label: 'Dividends', icon: DollarSign, color: 'text-amber-600 dark:text-amber-400', borderHover: 'hover:border-amber-500/40', path: '/dividend-dashboard' },
                { key: 'perf', label: 'Performance', icon: Activity, color: 'text-blue-600 dark:text-blue-400', borderHover: 'hover:border-blue-500/40', path: '/performance-tracker' },
                { key: 'watch', label: 'Watchlist', icon: Bell, color: 'text-rose-600 dark:text-rose-400', borderHover: 'hover:border-rose-500/40', path: '/price-alerts' },
                { key: 'risk', label: 'Risk', icon: ShieldAlert, color: 'text-red-600 dark:text-red-400', borderHover: 'hover:border-red-600/40', path: '/risk-dashboard' },
                { key: 'earn', label: 'Earnings', icon: CalendarDays, color: 'text-orange-600 dark:text-orange-400', borderHover: 'hover:border-orange-500/40', path: '/earnings-calendar' },
                { key: 'macro', label: 'Macro Indicators', icon: Globe, color: 'text-indigo-600 dark:text-indigo-400', borderHover: 'hover:border-indigo-500/40', path: '/macro-indicators' },
                { key: 'capitol', label: 'Capitol Trades', icon: Landmark, color: 'text-amber-600 dark:text-amber-400', borderHover: 'hover:border-amber-500/40', path: '/capitol-trades' },
                { key: 'weak', label: 'Weak Sentiments', icon: Eye, color: 'text-violet-600 dark:text-violet-400', borderHover: 'hover:border-violet-500/40', path: '/weak-sentiments' },
                { key: 'spg', label: 'Price Gathering', icon: LineChart, color: 'text-sky-600 dark:text-sky-400', borderHover: 'hover:border-sky-500/40', path: '/stock-price-gathering', activeRing: 'ring-sky-500/50 border-sky-500 bg-sky-500/5 shadow-sky-500/20' },
                { key: 'help', label: 'Help', icon: HelpCircle, color: 'text-slate-600 dark:text-slate-400', borderHover: 'hover:border-slate-500/40', path: '/help' },
              ]) as Array<{ key: string; label: string; icon: any; color: string; borderHover: string; path?: string; onClick?: () => void; iconClass?: string; activeRing?: string }>).map(({ key, label, icon: Icon, color, borderHover, path, onClick, iconClass, activeRing }) => {
                const isActive = !!path && location.pathname === path;
                const handleActivate = onClick ?? (path ? () => navigate(path) : undefined);
                return (
                  <Card
                    key={key}
                    role="button"
                    tabIndex={handleActivate ? 0 : -1}
                    aria-label={label}
                    aria-current={isActive ? 'page' : undefined}
                    className={cn(
                      'cursor-pointer hover:shadow-xl hover:scale-105 transition-all duration-300 group bg-card/90 backdrop-blur-sm',
                      'outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                      borderHover,
                      isActive && (activeRing
                        ? `border ring-2 shadow-lg scale-105 ${activeRing}`
                        : 'border-primary ring-2 ring-primary/40 shadow-lg shadow-primary/10 scale-105 bg-primary/5'),
                    )}
                    onClick={handleActivate}
                    onKeyDown={(e) => {
                      if (handleActivate && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        handleActivate();
                      }
                    }}
                  >
                    <CardContent className="p-6 md:p-8 flex flex-col items-center justify-center text-center h-[160px] sm:h-[170px] md:h-[180px] lg:h-[190px]">
                      <div className="p-4 md:p-5 bg-muted/50 rounded-2xl mb-3 group-hover:bg-muted group-hover:scale-110 transition-all duration-300 flex items-center justify-center">
                        <Icon className={cn('h-8 w-8 md:h-10 md:w-10', color, iconClass)} />
                      </div>
                      <span className={cn(
                        'text-sm md:text-base font-semibold leading-tight',
                        isActive ? color : 'text-foreground',
                      )}>{label}</span>
                      <span className={cn(
                        'mt-1 text-[10px] uppercase tracking-wider font-bold h-3.5 leading-3.5',
                        isActive ? color : 'opacity-0',
                      )}>{isActive ? 'Active' : '\u00A0'}</span>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Platform Modules Showcase */}
          <div className="w-full max-w-6xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px flex-1 bg-border" />
              <h2 className="text-sm font-semibold uppercase tracking-widest text-muted-foreground">Platform Modules</h2>
              <div className="h-px flex-1 bg-border" />
            </div>

            {/* Search & filter */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-5">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                <Input
                  type="text"
                  value={moduleSearch}
                  onChange={(e) => setModuleSearch(e.target.value)}
                  placeholder="Search modules by name or keyword (e.g. hedge, dividend, earnings)..."
                  className="pl-9 pr-9 h-10 bg-card/60 backdrop-blur-sm"
                  aria-label="Search platform modules"
                />
                {moduleSearch && (
                  <button
                    type="button"
                    onClick={() => setModuleSearch('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md hover:bg-muted/60 text-muted-foreground"
                    aria-label="Clear search"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {[
                  { id: 'all', label: 'All' },
                  { id: 'portfolio', label: 'Portfolio' },
                  { id: 'analysis', label: 'Analysis' },
                  { id: 'market', label: 'Market & Intel' },
                  { id: 'strategy', label: 'Strategy & Ops' },
                ].map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setModuleCategory(c.id)}
                    className={cn(
                      'px-3 py-1.5 text-xs rounded-full border transition-all',
                      moduleCategory === c.id
                        ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                        : 'bg-card/60 border-border text-muted-foreground hover:text-foreground hover:border-primary/40'
                    )}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
            {moduleMatchCount !== null && (
              <div className="text-xs text-muted-foreground mb-3 px-1">
                {moduleMatchCount === 0
                  ? 'No modules match your filters.'
                  : `${moduleMatchCount} module${moduleMatchCount === 1 ? '' : 's'} matched`}
              </div>
            )}

            {import.meta.env.DEV && <ModuleAuditPanel />}

            <div ref={modulesGridRef} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {DASHBOARD_MODULES.map((m) => {
                const onActivate =
                  m.action === 'refresh-prices'
                    ? handleRefreshPrices
                    : m.action === 'open-excel-import'
                    ? () => document.getElementById('excel-import-trigger')?.click()
                    : m.route
                    ? () => navigate(m.route!)
                    : () => {};
                const isActive = m.route ? location.pathname === m.route : false;
                return (
                  <div key={m.id} data-category={m.category} data-module-id={m.id}>
                    <DashboardModuleCard
                      module={m}
                      onActivate={onActivate}
                      active={isActive}
                    />
                  </div>
                );
              })}
            </div>
          </div>
          <div className="hidden">
            <ExcelImport onImportComplete={loadPositions} />
          </div>
          
          {/* Action Buttons Row */}
          <div className="flex flex-wrap items-center gap-3">
            <AddPositionDialog onAddPosition={handleAddPosition} />
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm" disabled={positions.length === 0} className="text-destructive border-destructive/30 hover:bg-destructive/10">
                  <Trash2 className="mr-2 h-4 w-4" />
                  Reset Data
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will permanently delete all {positions.length} of your imported position{positions.length !== 1 ? 's' : ''} from your account. This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleResetData} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                    Delete My Data
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
        
        {/* Portfolio Stats */}
        {analyses.length > 0 && (
          <>
            <div className="mb-8">
              <PortfolioStats {...stats} alertCounts={alertCounts} />
            </div>
            
            {/* Stock Table - All Positions */}
            <div className="mb-8">
              <StockTable 
                analyses={sortedAnalyses}
                onRowClick={scrollToCard}
              />
            </div>
            
            {/* Stock Scorecards - Action Recommendations */}
            <div className="mb-8">
              <StockScorecard analyses={sortedAnalyses} />
            </div>
            
            {/* Fibonacci Retracement Calculator */}
            <div className="mb-8">
              <FibonacciRetracement />
            </div>
            
            {/* Sort Controls for Position Cards */}
            <div className="flex items-center gap-3 mb-6 mt-8">
              <ArrowUpDown className="h-5 w-5 text-muted-foreground" />
              <span className="text-sm font-medium text-foreground">Sort cards by:</span>
              <Select value={sortBy} onValueChange={(value) => setSortBy(value as SortOption)}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="name">Name</SelectItem>
                  <SelectItem value="totalValue">Total Value</SelectItem>
                  <SelectItem value="gainLoss">Gain/Loss</SelectItem>
                  <SelectItem value="addedValue">Added Value (CHF)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </>
        )}
        
        {/* Positions Grid */}
        {analyses.length === 0 && positions.length === 0 ? (
          <div className="text-center py-16">
            <div className="inline-block p-6 bg-muted rounded-full mb-4">
              <TrendingUp className="h-12 w-12 text-muted-foreground" />
            </div>
            <h2 className="text-2xl font-semibold text-foreground mb-2">
              No Positions Yet
            </h2>
            <p className="text-muted-foreground mb-6">
              Add your first stock position to start tracking performance
            </p>
            <AddPositionDialog onAddPosition={handleAddPosition} />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {sortedAnalyses.map(analysis => (
                <div 
                  key={analysis.id}
                  ref={(el) => (cardRefs.current[analysis.id] = el)}
                  className="transition-all duration-300"
                >
                  <PositionCard
                    analysis={analysis}
                    onDelete={handleDeletePosition}
                  />
                </div>
              ))}
            </div>
            
            {/* Added Value Section - Clear Visual Separation */}
            <div className="mt-40 pt-12 border-t-4 border-primary/20 relative z-0">
              <div className="mb-10">
                <h2 className="text-4xl font-bold text-foreground flex items-center gap-3 mb-3">
                  <TrendingUp className="h-10 w-10 text-primary" />
                  Portfolio Added Value Analysis
                </h2>
                <p className="text-lg text-muted-foreground">
                  Detailed breakdown of stocks contributing positive value to your portfolio
                </p>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                {/* Summary Statistics Cards */}
                <Card className="shadow-lg">
                  <CardHeader className="pb-4">
                    <CardTitle className="text-2xl">Portfolio Summary</CardTitle>
                    <CardDescription>Key metrics from top performers</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {(() => {
                        const positiveStocks = sortedAnalyses.filter(a => (a.addedValueChf || 0) > 0);
                        const totalAddedValue = positiveStocks.reduce((sum, a) => sum + (a.addedValueChf || 0), 0);
                        const avgGain = positiveStocks.length > 0 
                          ? (positiveStocks.reduce((sum, a) => sum + ((a.gainPct || 0) * 100), 0) / positiveStocks.length) 
                          : 0;
                        
                        return (
                          <>
                            <div className="flex justify-between items-center p-4 bg-primary/5 rounded-lg">
                              <span className="font-medium text-foreground">Total Profitable Stocks</span>
                              <span className="text-2xl font-bold text-primary">{positiveStocks.length}</span>
                            </div>
                            <div className="flex justify-between items-center p-4 bg-green-500/10 rounded-lg">
                              <span className="font-medium text-foreground">Total Added Value</span>
                              <span className="text-2xl font-bold text-green-600">CHF {totalAddedValue.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between items-center p-4 bg-blue-500/10 rounded-lg">
                              <span className="font-medium text-foreground">Average Gain</span>
                              <span className="text-2xl font-bold text-blue-600">+{avgGain.toFixed(2)}%</span>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </CardContent>
                </Card>
                
                {/* Pie Chart */}
                <div className="h-full">
                  <PortfolioGainsPieChart analyses={analyses} />
                </div>
              </div>
              
              {/* Contributors Table */}
              <Card className="shadow-lg">
                <CardHeader className="pb-4">
                  <CardTitle className="text-2xl flex items-center gap-2">
                    <TrendingUp className="h-6 w-6 text-primary" />
                    Contributors
                  </CardTitle>
                  <CardDescription>All stocks with positive added value, color-coded by performance</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b-2 border-border">
                          <th className="text-left py-4 px-4 font-bold text-foreground">Rank</th>
                          <th className="text-left py-4 px-4 font-bold text-foreground">Stock</th>
                          <th className="text-right py-4 px-4 font-bold text-foreground">Added Value (CHF)</th>
                          <th className="text-right py-4 px-4 font-bold text-foreground">Gain %</th>
                          <th className="text-right py-4 px-4 font-bold text-foreground">Total Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const contributors = sortedAnalyses
                            .filter(a => (a.addedValueChf || 0) > 0)
                            .sort((a, b) => (b.addedValueChf || 0) - (a.addedValueChf || 0));
                          
                          const total = contributors.length;
                          const topThird = Math.ceil(total / 3);
                          const middleThird = Math.ceil((total * 2) / 3);
                          
                          return contributors.map((analysis, index) => {
                            const currencySymbol = analysis.localCurrency === 'USD' ? '$' : 
                                                   analysis.localCurrency === 'EUR' ? '€' : 'CHF';
                            
                            // Color coding based on rank
                            let rowClass = 'bg-yellow-50/30 dark:bg-yellow-900/10'; // Lowest (yellow)
                            let rankBgClass = 'bg-yellow-500/20 text-yellow-700 dark:text-yellow-400';
                            
                            if (index < topThird) {
                              // Best performers (green)
                              rowClass = 'bg-green-50/50 dark:bg-green-900/20';
                              rankBgClass = 'bg-green-500/30 text-green-800 dark:text-green-400';
                            } else if (index < middleThird) {
                              // Middle performers (blue)
                              rowClass = 'bg-blue-50/30 dark:bg-blue-900/10';
                              rankBgClass = 'bg-blue-500/20 text-blue-700 dark:text-blue-400';
                            }
                            
                            return (
                              <tr key={analysis.id} className={`border-b border-border/30 hover:brightness-95 transition-all ${rowClass}`}>
                                <td className="py-4 px-4">
                                  <div className={`flex items-center justify-center w-8 h-8 rounded-full font-bold ${rankBgClass}`}>
                                    {index + 1}
                                  </div>
                                </td>
                                <td className="py-4 px-4">
                                  <div className="font-semibold text-foreground">{analysis.ticker}</div>
                                </td>
                                <td className="py-4 px-4 text-right">
                                  <span className="text-lg font-bold text-green-700 dark:text-green-400">
                                    CHF {(analysis.addedValueChf || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </span>
                                </td>
                                <td className="py-4 px-4 text-right">
                                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 animate-blink">
                                    +{((analysis.gainPct || 0) * 100).toFixed(2)}%
                                  </span>
                                </td>
                                <td className="py-4 px-4 text-right">
                                  <span className="font-medium text-muted-foreground">
                                    {currencySymbol} {(analysis.totalValue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </span>
                                </td>
                              </tr>
                            );
                          });
                        })()}
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-6 flex items-center gap-6 justify-center text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded bg-green-500/30"></div>
                      <span className="text-muted-foreground">Best Performers</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded bg-blue-500/20"></div>
                      <span className="text-muted-foreground">Middle Performers</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded bg-yellow-500/20"></div>
                      <span className="text-muted-foreground">Lower Contributors</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </div>
      
      {/* AI Chat Widget */}
      <AIChatWidget />
    </div>
  );
};

export default Index;
